import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Hero() {
  const { t } = useTranslation('deneigement');
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.1);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-[70vh] md:min-h-[75vh] flex items-center justify-center overflow-hidden pt-10"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Quebec residential house with massive heavy snow accumulation on roof, dangerous ice dam along roof edge, icicles hanging, winter storm aftermath, grey overcast winter sky, heavy snow on ground, dramatic winter photography, cold blue and white tones, no text visible, editorial quality, landscape composition"
          alt="Maison québécoise avec accumulation de neige sur le toit et barrage de glace nécessitant déneigement à Lévis"
          className="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
          width="1920"
          height="800"
          fetchpriority="high"
          width="1920"
          height="1080"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-primary/70 via-slate-primary/50 to-slate-primary/75" />
      </div>

      {/* Content */}
      <div
        ref={ref}
        className={`relative z-10 w-full px-4 md:px-6 lg:px-10 pt-16 pb-16 max-w-4xl mx-auto text-center transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Breadcrumb */}
        <nav className="mb-6" aria-label="Fil d'Ariane">
          <ol className="flex items-center justify-center gap-2 text-sm text-white/70">
            <li>
              <button
                onClick={() => {
                  if (window.REACT_APP_NAVIGATE) window.REACT_APP_NAVIGATE('/');
                }}
                className="hover:text-white transition-colors"
              >
                {t('breadcrumb_home')}
              </button>
            </li>
            <li><i className="ri-arrow-right-s-line" /></li>
            <li className="text-white font-medium">{t('breadcrumb_current')}</li>
          </ol>
        </nav>

        <h1 className="font-manrope font-extrabold text-white text-[40px] sm:text-4xl md:text-5xl lg:text-[56px] leading-tight mb-5">
          {t('hero_h1')}
        </h1>

        <p className="text-white/85 text-base md:text-lg lg:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          {t('hero_subtitle')}
        </p>

        {/* 2 CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
          <a
            href="tel:+14184769950"
            aria-label="Appeler Couvreur-Lévis.com au 418-476-9950"
            title="Cliquez pour appeler"
            className="flex items-center gap-3 bg-blue-ice hover:bg-[#3a7bc8] text-white font-semibold px-8 py-4 rounded-md text-base md:text-lg transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-phone-line" />
            {t('hero_cta_call')} {t('cta_phone')}
          </a>
          <button
            onClick={() => scrollToSection('quote-form')}
            className="group bg-copper hover:bg-copper-dark text-white font-semibold px-8 py-4 rounded-md text-base md:text-lg transition-all duration-300 flex items-center gap-3 whitespace-nowrap"
          >
            {t('hero_cta_quote')}
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-time-line text-blue-ice" />
            <span className="text-white text-sm font-medium">{t('hero_stat_1')}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-snowy-line text-blue-ice" />
            <span className="text-white text-sm font-medium">{t('hero_stat_2')}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-shield-check-line text-blue-ice" />
            <span className="text-white text-sm font-medium">{t('hero_stat_3')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}